import { utils, writeFile } from 'xlsx';
import { Project } from '../../../domain/project/Project';

import { stopConstraints } from './constraints';
import { driverLocations, stopLocations } from './locations';

const manhattanDistance = (position1: { lat: number; lng: number }, position2: { lat: number; lng: number }) => {
  return Math.abs(position1.lat - position2.lat) + Math.abs(position1.lng - position2.lng);
};

const closestIndex = (position: { lat: number; lng: number }, positions: { lat: number; lng: number }[]): number => {
  let closestIdx = 0;
  let closestDistance = 99999;
  positions.forEach((positionDest, idx) => {
    const dist = manhattanDistance(position, positionDest);
    if (dist < closestDistance) {
      closestIdx = idx;
      closestDistance = dist;
    }
  });
  return closestIdx;
};

interface IStop {
  name: string;
  address: string;
  address_details: string;
  delivery_name?: string;
  delivery_address?: string;
  delivery_address_details?: string;
  time_windows?: string;
  volume?: number;
  weight?: number;
}

interface IDriver {
  name: string;
  start_location: string;
  end_location: string;
  time_windows?: string;
  volume?: number;
  weight?: number;
}

export const getStopExamples = (position: { lat: number; lng: number }, project: Project): IStop[] => {
  // check the closest country
  const closestIdx = closestIndex(
    position,
    stopLocations.map((l) => l.location)
  );
  const closestLocation = stopLocations[closestIdx];

  const { label, address, address_details, delivery_address, delivery_address_details, delivery_label } =
    closestLocation.stops;

  const excelJson: IStop[] = [];

  address.forEach((addr, idx) => {
    excelJson.push({
      name: label[idx],
      address: addr,
      address_details: address_details[idx],
      ...(project.constraints.pickups
        ? {
            delivery_name: delivery_label[idx],
            delivery_address: delivery_address[idx],
            delivery_address_details: delivery_address_details[idx],
          }
        : {}),
      ...(project.constraints.time_windows
        ? {
            time_windows: stopConstraints.time_windows[idx],
          }
        : {}),
      ...(project.constraints.volume
        ? {
            volume: stopConstraints.volume[idx],
          }
        : {}),
      ...(project.constraints.weight
        ? {
            weight: stopConstraints.weight[idx],
          }
        : {}),
    });
  });

  return excelJson;
};

export const getDriverExamples = (position: { lat: number; lng: number }, project: Project): IDriver[] => {
  // check the closest country
  const closestIdx = closestIndex(
    position,
    driverLocations.map((l) => l.location)
  );
  const closestLocation = driverLocations[closestIdx];

  const { label, end_address, start_address } = closestLocation.drivers;

  const excelJson: IDriver[] = [];

  label.forEach((lbl, idx) => {
    excelJson.push({
      name: lbl,
      start_location: start_address[idx],
      end_location: end_address[idx],
      time_windows: stopConstraints.time_windows[idx],
      ...(project.constraints.volume
        ? {
            volume: stopConstraints.volume[idx],
          }
        : {}),
      ...(project.constraints.weight
        ? {
            weight: stopConstraints.weight[idx],
          }
        : {}),
    });
  });

  return excelJson;
};

export const exportToExcel = (csvData: any, fileName: string) => {
  const wb = utils.book_new();
  wb.Props = {
    Title: fileName,
  };
  wb.SheetNames.push(`data`);
  const ws = utils.json_to_sheet(csvData);
  wb.Sheets[`data`] = ws;
  writeFile(wb, `${fileName}.xlsx`, { bookSST: true });
};
